import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { faMailBulk, faPhone } from "@fortawesome/free-solid-svg-icons";
import { FormBuilder, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { MapBoxService } from "../../_services/mapbox.service";
import { CustomerService, ProjectService, StoreService } from "../../_services";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { APProject } from "../../_models/project.interface";
import * as mapboxgl from "mapbox-gl";
import { PAUtil } from "../planning-assistant/classes/util";
import { GeocoderEvent } from "../../_models/mapbox.interface";
import { APCustomer } from "../../_models/customer.interface";
import { APStore, PermittedStoreParams } from 'src/app/_models/store.interface';
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import { RecordNavigationComponent } from "../_shared/record-navigation/record-navigation.component";
import { createStoreNrValidator } from "../_shared/custom-form-validators/store-form-validators";
import { NgTemplateOutlet } from "@angular/common";
import { NgSelectModule } from '@ng-select/ng-select';

@Component({
  selector: 'hawk-store-create',
  templateUrl: './store-create.component.html',
  standalone: true,
  imports: [
    RouterLink,
    RecordNavigationComponent,
    ReactiveFormsModule,
    NgTemplateOutlet,
    NgSelectModule
  ],
  styleUrls: ['./store-create.component.scss'],
  host: {
    class: 'app-page'
  }
})
export class StoreCreateComponent implements OnInit, OnDestroy {
  private mapBoxService = inject(MapBoxService)
  private customerService = inject(CustomerService)
  private storeService = inject(StoreService)
  private projectService = inject(ProjectService)
  private router = inject(Router)
  private route = inject(ActivatedRoute)
  
  protected readonly faMailBulk = faMailBulk;
  protected readonly faPhone = faPhone;
  private fb = inject(FormBuilder);

  customers: APCustomer[] = []
  projects: APProject[] = []

  /**
   * Kunden und Projekte können über Query-Parameter vorgegeben werden — so
   * verlinkt der Filial-Picker hierher, wenn die gesuchte Filiale noch fehlt.
   * Solange die Vorgabe gilt, sind beide Felder gesperrt; über "Auswahl ändern"
   * lässt sie sich verwerfen.
   */
  protected presetFromRoute = false
  private customerSubscription?: Subscription

  addressDuplicates: APStore[] = []
  addressDuplicateCheckLoading = false
  showDuplicateModal = false
  showConfirmModal = false

  addressGeocoder?: MapboxGeocoder

  storeForm = this.fb.group({
    nr: this.fb.control("", {
      validators: [Validators.required],
      asyncValidators: [createStoreNrValidator(this.storeService)]
    }),
    customer_ids: this.fb.control<number[]>({value: [], disabled: false}),
    project_ids: this.fb.control<number[]>({value: [], disabled: false}),
    name: this.fb.control("", [Validators.required]),
    email: this.fb.control("", [Validators.email]),
    comment: this.fb.control(""),
    phone1: this.fb.control("", Validators.pattern('[- +()0-9]+')),
    phone2: this.fb.control("", Validators.pattern('[- +()0-9]+')),
    register: this.fb.control({value: 0, disabled: false}),
    ip: this.fb.control(""),
    company: this.fb.control(""),
    street: this.fb.control({value: '', disabled: true}),
    zip: this.fb.control({value: '', disabled: true}, [Validators.pattern('^([a-zA-Z]{2}-)?[0-9]{2,5}( [a-zA-Z]{0,2})?$')]),
    city: this.fb.control({value: '', disabled: true}),
    country: this.fb.control({value: '', disabled: true}),
    state: this.fb.control({value: '', disabled: true}),
    lat: this.fb.control<number>(null),
    lng: this.fb.control<number>(null),
    address_firstname: this.fb.control(""),
    address_lastname: this.fb.control(""),
  });

  ngOnInit(): void {
    this.loadCustomers()
    this.loadProjects()
    this.resetForm()
    this.applyRoutePreset()
    this.customerSubscription = this.storeForm.controls.customer_ids.valueChanges.subscribe(
      () => this.onCustomersChanged()
    )
    this.setGeocoder()
  }

  ngOnDestroy(): void {
    this.customerSubscription?.unsubscribe()
  }

  // --- Kunden-/Projekt-Verknüpfung ---

  /**
   * Projekte, die zur Auswahl stehen. Ohne Kundenauswahl bleibt die Liste leer —
   * eine Projekt-Verknüpfung ohne Kunden ergibt fachlich keinen Sinn.
   *
   * Bewusst ein Feld und kein Getter: ng-select würde bei einem Getter in jedem
   * Change-Detection-Zyklus eine neue Array-Referenz sehen und die Liste neu aufbauen.
   */
  protected selectableProjects: APProject[] = []

  /** Namen der vorgegebenen Projekte für den Hinweistext */
  protected presetProjectNames: string[] = []

  /**
   * Die einzig gültigen Projekte: aktive Projekte der aktuell gewählten Kunden.
   * Einzige Quelle sowohl für die Auswahlliste als auch für das Aussortieren beim
   * Speichern — damit kann die Anzeige nicht von dem abweichen, was gesendet wird.
   */
  private validProjects(): APProject[] {
    const customerIds = this.storeForm.controls.customer_ids.value || []
    if (!customerIds.length) return []

    return this.projects.filter(project => project.active && customerIds.includes(project.customer?.id))
  }

  private refreshProjectSelection(): void {
    const valid = this.validProjects()
    const selectedIds = this.storeForm.controls.project_ids.value || []

    // Vorgegebene Projekte müssen anzeigbar bleiben, auch wenn sie inaktiv sind
    // oder ihre Kunden noch nicht aufgelöst wurden.
    const preserved = this.presetFromRoute
      ? this.projects.filter(
          project => selectedIds.includes(project.id) && !valid.some(v => v.id === project.id)
        )
      : []

    this.selectableProjects = [...valid, ...preserved]
    this.presetProjectNames = selectedIds.map(
      id => this.projects.find(project => project.id === id)?.name || `#${id}`
    )
  }

  /**
   * Vorgabe verwerfen: Felder leeren und wieder freigeben, damit Kunden und
   * Projekte frei gewählt werden können.
   */
  protected clearRoutePreset(): void {
    this.presetFromRoute = false
    this.pendingCustomerDerivation = []
    this.storeForm.controls.customer_ids.reset([])
    this.storeForm.controls.project_ids.reset([])
    this.storeForm.controls.customer_ids.enable()
    this.storeForm.controls.project_ids.enable()
    this.refreshProjectSelection()
  }

  /**
   * Kundenwechsel: Auswahlliste neu aufbauen und Projekte abwählen, die nicht mehr
   * dazu passen. Hängt bewusst an valueChanges statt am (change)-Output von
   * ng-select — so greift es auch bei programmatischen Änderungen.
   */
  private onCustomersChanged(): void {
    this.refreshProjectSelection()

    // Bei einer Vorgabe aus dem Link ist die Projektauswahl gesetzt und gesperrt,
    // die darf das Ableiten der Kunden nicht wieder wegräumen.
    if (this.presetFromRoute) return

    const projectControl = this.storeForm.controls.project_ids
    const pruned = this.prunedProjectIds()

    if (pruned.length !== (projectControl.value || []).length) {
      projectControl.setValue(pruned)
      this.refreshProjectSelection()
    }
  }

  /** Ausgewählte Projekte ohne die, deren Kunde nicht (mehr) gewählt ist. */
  private prunedProjectIds(): number[] {
    const validIds = this.validProjects().map(project => project.id)

    return (this.storeForm.controls.project_ids.value || []).filter(id => validIds.includes(id))
  }

  private applyRoutePreset(): void {
    const params = this.route.snapshot.queryParamMap
    const projectIds = this.parseIdList(params.getAll('project_ids'))
    const customerIds = this.parseIdList(params.getAll('customer_ids'))

    // Namensvorschlag aus der Filialsuche — bewusst frei editierbar und
    // unabhängig von der Kunden-/Projekt-Sperre.
    const name = params.get('name')?.trim()
    if (name) this.storeForm.controls.name.setValue(name)

    if (!projectIds.length && !customerIds.length) return

    this.presetFromRoute = true
    this.storeForm.controls.project_ids.setValue(projectIds)
    this.storeForm.controls.customer_ids.setValue(customerIds)
    this.storeForm.controls.customer_ids.disable()
    this.storeForm.controls.project_ids.disable()

    // Fehlen die Kunden im Link, ergeben sie sich aus den Projekten — ein
    // Projekt gehört immer genau einem Kunden.
    if (!customerIds.length) {
      this.pendingCustomerDerivation = projectIds
    }

    this.refreshProjectSelection()
  }

  /** Projekt-IDs, aus denen die Kunden noch abgeleitet werden müssen (Projekte laden asynchron). */
  private pendingCustomerDerivation: number[] = []

  private deriveCustomersFromProjects(): void {
    if (!this.pendingCustomerDerivation.length) return

    const customerIds = [
      ...new Set(
        this.pendingCustomerDerivation
          .map(id => this.projects.find(project => project.id === id)?.customer?.id)
          .filter((id): id is number => !!id)
      )
    ]

    this.storeForm.controls.customer_ids.setValue(customerIds)
    this.pendingCustomerDerivation = []
    this.refreshProjectSelection()
  }

  private parseIdList(values: string[]): number[] {
    return values
      .flatMap(value => value.split(','))
      .map(value => Number.parseInt(value, 10))
      .filter(id => Number.isFinite(id))
  }

  loadProjects(): void {
    this.projectService.getAPProjects().subscribe({
      next: projects => {
        this.projects = [...projects].sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1))
        this.refreshProjectSelection()
        this.deriveCustomersFromProjects()
      },
      error: err => {
        console.log(err)
      }
    })
  }

  get canSubmit(): boolean {
    return this.storeForm.valid && !this.addressDuplicateCheckLoading
  }

  hasError = (control: string, error: string) => {
    return this.storeForm.controls[control].hasError(error);
  }

  getError = (control: string, error: string) => {
    return this.storeForm.controls[control].getError(error);
  }

  loadCustomers(): void {
    this.customerService.loadAPCustomers().subscribe(
      data => {
        this.customers = data
      },
      err => {
        console.log(err)
      }
    )
  }

  resetForm():void {
    this.storeForm.setValue({
      nr: '',
      customer_ids: [],
      project_ids: [],
      name: '',
      email: '',
      comment: '',
      phone1: '',
      phone2: '',
      register: 0,
      ip: '',
      company: '',
      street: '',
      zip: '',
      city: '',
      country: '',
      state: '',
      lat: 0,
      lng: 0,
      address_firstname: '',
      address_lastname: '',
    });
  }

  public async setGeocoder(): Promise<void> {
    this.addressGeocoder = new MapboxGeocoder({
      accessToken: this.mapBoxService.accessToken,
      marker: false,
      language: 'de-DE',
      mapboxgl: mapboxgl,
      placeholder: 'Adresse suchen'
    })
    while (!document.getElementById('address_geocoder')) {
      await PAUtil.sleep(100)
    }
    const address_geocoder_elem = document.getElementById('address_geocoder')
    this.addressGeocoder.addTo(address_geocoder_elem);

    this.addressGeocoder.on('result', (event) => {
      this.onAddressGeocoderEvent(event)
    })
  }

  private onAddressGeocoderEvent(event: GeocoderEvent) {
    const {country, region, city, post_code, street, street_no, center, name} = PAUtil.getGeocoderEventAddress(event);

    const address_country_control = this.storeForm.controls['country']
    address_country_control.setValue(country)
    address_country_control.markAsTouched();
    address_country_control.updateValueAndValidity()
    const address_state_control = this.storeForm.controls['state']
    address_state_control.setValue(region)
    address_state_control.markAsTouched();
    address_state_control.updateValueAndValidity()
    const address_city_control = this.storeForm.controls['city']
    address_city_control.setValue(city)
    address_city_control.markAsTouched();
    address_city_control.updateValueAndValidity()
    const address_zip_control = this.storeForm.controls['zip']
    address_zip_control.setValue(post_code)
    address_zip_control.markAsTouched();
    address_zip_control.updateValueAndValidity()
    const address_street_control = this.storeForm.controls['street']
    address_street_control.setValue(street + ' ' + street_no)
    address_street_control.markAsTouched();
    address_street_control.updateValueAndValidity()

    const latitude_control = this.storeForm.controls['lat']
    latitude_control.setValue(center[1])
    latitude_control.markAsTouched();
    latitude_control.updateValueAndValidity()
    const longitude_control = this.storeForm.controls['lng']
    longitude_control.setValue(center[0])
    longitude_control.markAsTouched();
    longitude_control.updateValueAndValidity()

    this.storeForm.updateValueAndValidity()

    // Duplikat-Prüfung nach Adress-Auswahl
    this.addressDuplicates = []
    if (post_code || street) {
      this.checkAddressDuplicates(post_code, (street + ' ' + street_no).trim())
    }
  }

  private checkAddressDuplicates(zip: string, street: string): void {
    this.addressDuplicateCheckLoading = true
    this.storeService.checkAddressDuplicate(zip, street).subscribe({
      next: (stores) => {
        this.addressDuplicates = stores
        this.addressDuplicateCheckLoading = false
      },
      error: () => {
        this.addressDuplicateCheckLoading = false
      }
    })
  }

  onSubmit() {
    this.showConfirmModal = true
  }

  async confirmCreate() {
    this.showConfirmModal = false
    const store_id = await this.createStore()
    if (store_id) {
      this.router.navigate(['/filiale', store_id])
    }
  }

  createStore(): Promise<number> {
    return new Promise<number>(resolve => {
      this.storeService.create(
        this.getCreateHash(),
        {customer_ids: this.getCustomerIds(), project_ids: this.getProjectIds()}
      ).subscribe(
        data => {
          resolve(data.id)
        },
        err => {
          console.log(err)
          resolve(null)
        }
      )
    })
  }

  getCreateHash(): PermittedStoreParams {
    const controls = this.storeForm.controls

    const street = controls['street'].value
    const zip_code = controls['zip'].value
    const city = controls['city'].value
    const country = controls['country'].value

    return {
      address_firstname: controls['address_firstname'].value,
      address_lastname: controls['address_lastname'].value,
      name: controls['name'].value,
      comment: controls['comment'].value,
      email: controls['email'].value,
      phone1: controls['phone1'].value,
      phone2: controls['phone2'].value,
      nr: controls['nr'].value,
      address_extra: null,
      address_street_no: null,
      address_company: controls['company'].value,
      address_street: street,
      address_city: city,
      address_zip: zip_code,
      address_country: country,
      address_state: controls['state'].value,
      address_latitude: controls['lat'].value,
      address_longitude: controls['lng'].value,
      address_used_google_maps_parameter: `${street}, ${zip_code} ${city}, ${country}`,
      register: controls['register'].value
    }
  }

  // Bei gesperrten Feldern liefert form.value nichts — der Control-Wert bleibt aber gesetzt.
  getCustomerIds(): number[] {
    return this.storeForm.controls.customer_ids.value || []
  }

  /**
   * Beim Speichern nochmals gegen die Kundenauswahl gefiltert. Damit kann nie eine
   * Projekt-Verknüpfung mitgehen, die in der Maske gar nicht mehr sichtbar ist —
   * unabhängig davon, ob ein UI-Event unterwegs verloren gegangen ist.
   * Eine Vorgabe aus dem Link ist gesetzt und gesperrt und bleibt unangetastet.
   */
  getProjectIds(): number[] {
    if (this.presetFromRoute) return this.storeForm.controls.project_ids.value || []

    return this.prunedProjectIds()
  }
}


